import { useMemo, useState, type ReactNode } from "react";
import { describeError } from "../api/http";
import { usePlan, useSquad, useUpdatePlan } from "../api/queries";
import type { SquadPlayer } from "../api/types";
import { Page } from "../components/layout/Page";
import {
  PlayerList,
  type PlayerCardData,
  type PlayerColumn,
} from "../components/PlayerCard";
import { Button, ButtonLink } from "../components/ui/Button";
import { ToggleChip } from "../components/ui/Chip";
import { Banner, EmptyState, ErrorState } from "../components/ui/ErrorState";
import { Skeleton } from "../components/ui/Skeleton";
import { count, dash, delta, money, DASH } from "../lib/format";
import {
  addToPlan,
  planSideOf,
  removeFromPlan,
  toPlanDraft,
  type PlanDraft,
  type PlanSide,
} from "../lib/plan";
import { POSITIONS, POSITION_GROUPS, type Position } from "../lib/positions";

/**
 * Pantalla *Plantilla* — M8-T02 (UC-08.02).
 *
 * QUÉ ENSEÑA
 *   Mis jugadores agrupados por posición, cada uno con su ganancia desde la compra, sus puntos
 *   esperados, su rango en la posición, su tendencia y su estado. Encima, la barra de orden; en el
 *   cuerpo, el interruptor *Ver con plan*; en cada ficha, la acción de plan y el enlace a la ficha
 *   completa. A partir de 768 px la misma información es una tabla (sección 8.1), y de eso se
 *   ocupa `PlayerList`, que monta las dos mitades y deja que el CSS elija.
 *
 * POR QUÉ EL ORDEN SE HACE AQUÍ Y NO EN EL SERVIDOR
 *   `GET /api/squad` de la sección 9.1 solo acepta `plan=0|1`: no tiene parámetro de orden, al
 *   contrario que `GET /api/market`. Y no podría tenerlo tal cual está pedido, porque una de las
 *   cuatro ordenaciones es **la ganancia**, que no es una métrica del catálogo sino la diferencia
 *   entre mi precio de compra y el precio de hoy. Así que la plantilla —quince jugadores— se
 *   ordena en el cliente. La consecuencia es la que pide la aceptación, y en su forma más fuerte:
 *   al cambiar el orden **no se pierde ni un dato**, porque no hay ninguna petición nueva que
 *   esperar; la lista se reordena en el sitio, sin esqueleto y sin vaciarse.
 *
 *   El interruptor *Ver con plan* sí cambia la consulta (`?plan=1` es otra clave de caché), y ahí
 *   `useSquad` trae `placeholderData: keepPreviousData` de M7-T02: mientras llega la plantilla
 *   virtual se siguen viendo los jugadores anteriores con opacidad reducida (sección 8.2). Es el
 *   mismo contrato para los dos controles de la pantalla.
 *
 * QUÉ CAMBIA EL INTERRUPTOR
 *   Apagado: mi plantilla real y el **presupuesto de hoy**; los jugadores que el plan vende salen
 *   marcados. Encendido: la plantilla virtual de la sección 7.5 —sin las ventas y con las
 *   compras— y el **presupuesto después del plan**. Las dos cifras vienen siempre en la misma
 *   respuesta (`budget` y `budgetAfterPlan`), así que el interruptor no las inventa: elige cuál se
 *   lee, que es lo que hace visible el efecto del plan.
 *
 * LA ACCIÓN DE PLAN MIRA EL PLAN, NO SOLO `inPlan`
 *   `SquadPlayer.inPlan` solo vale `"sell" | null`, y con `?plan=1` los vendidos ya no están en la
 *   lista mientras que los **comprados** sí aparecen, con `inPlan: null`. Si la acción se decidiera
 *   solo con ese campo, a un fichaje planeado se le ofrecería *Vender en plan* y el plan pasaría a
 *   ser inválido por vender a quien todavía no es mío (sección 7.5). Por eso el lado del plan se
 *   consulta en `GET /api/plan`, que conoce las dos listas. Y por eso mismo la acción está
 *   deshabilitada hasta que esa consulta responde: `PUT /api/plan` manda el plan **entero**, así
 *   que guardar sin haberlo leído borraría lo que ya hubiera dentro.
 *
 * DÓNDE SE APARTA DEL MOCKUP
 *   `docs/mockups/plantilla.html` dibuja a partir de 768 px **una sola** tabla con los quince
 *   jugadores. Aquí hay una tabla por posición, porque las tarjetas van agrupadas por posición
 *   (así lo pide la tarea) y `PlayerList` es una pieza única que rinde tarjetas y tabla de la misma
 *   lista: partir el grupo en móvil y unirlo en escritorio obligaría a reescribir la tabla a mano.
 *   Cada tabla lleva el nombre del grupo en su `aria-label`, de modo que la agrupación se lee en
 *   los dos anchos y cuatro tablas cortas se recorren mejor que una de quince filas.
 */

/* ============================================================================== orden ==== */

export type SquadSort = "expected" | "price" | "trend" | "gain";

/** Los cuatro criterios de la tarea, con el texto exacto que se ve en el chip. */
export const SQUAD_SORTS: readonly { id: SquadSort; label: string }[] = [
  { id: "expected", label: "Puntos esperados" },
  { id: "price", label: "Precio" },
  { id: "trend", label: "Tendencia" },
  { id: "gain", label: "Ganancia" },
];

/**
 * El número por el que se ordena, o `null` cuando ese jugador no lo tiene: sin métricas
 * calculadas todavía, o sin precio de compra conocido (sección 4.4).
 */
function valorDeOrden(player: SquadPlayer, sort: SquadSort): number | null {
  switch (sort) {
    case "expected":
      return player.metrics?.expectedPpr ?? null;
    case "price":
      return player.price;
    case "trend":
      // La misma tendencia que se enseña en la tarjeta y en la columna «Tend. 7d».
      return player.metrics?.trend7d ?? null;
    case "gain":
      return player.gain;
  }
}

/**
 * De mayor a menor, con los datos ausentes al final y el nombre como desempate.
 *
 * El desempate se compara con `<` y no con `localeCompare`, por lo mismo que `lib/format.ts` no
 * usa `Intl`: la colación depende de la versión de ICU del equipo y el orden tiene que salir igual
 * en cualquier máquina. No muta la lista que llega, que es la de la caché de TanStack Query.
 */
export function ordenarPlantilla(
  players: readonly SquadPlayer[],
  sort: SquadSort,
): SquadPlayer[] {
  return [...players].sort((uno, otro) => {
    const valorUno = valorDeOrden(uno, sort);
    const valorOtro = valorDeOrden(otro, sort);

    if (valorUno === null && valorOtro !== null) return 1;
    if (valorUno !== null && valorOtro === null) return -1;
    if (valorUno !== null && valorOtro !== null && valorUno !== valorOtro) {
      return valorOtro - valorUno;
    }
    if (uno.name === otro.name) return uno.id - otro.id;
    return uno.name < otro.name ? -1 : 1;
  });
}

export interface SquadGroup {
  position: Position;
  players: SquadPlayer[];
}

/** Los cuatro grupos del campo, de la portería hacia arriba y sin los que no tienen a nadie. */
export function agruparPorPosicion(
  players: readonly SquadPlayer[],
): SquadGroup[] {
  return POSITIONS.map((position) => ({
    position,
    players: players.filter((player) => player.position === position),
  })).filter((grupo) => grupo.players.length > 0);
}

/* ============================================================================= piezas ==== */

/** Ganancia desde la compra: `+1,1 M`, `-400 k` o `—` si no se conoce el precio de compra. */
function ganancia(gain: number | null): ReactNode {
  if (gain === null) return <span className="text-muted">{DASH}</span>;
  return (
    <span className={gain < 0 ? "text-down" : "text-up"}>{delta(gain)}</span>
  );
}

interface InterruptorProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}

/**
 * *Ver con plan*: etiqueta y casilla en la misma fila de 44 px, para que se pueda pulsar en
 * cualquier punto (sección 8.1). Es una casilla nativa con `role="switch"`: el teclado y el lector
 * de pantalla ya saben qué hacer con ella y no hay ningún estado que sincronizar a mano.
 */
function Interruptor({ checked, onChange, children }: InterruptorProps) {
  return (
    <label className="flex min-h-touch cursor-pointer items-center justify-between gap-2.5">
      <span className="font-medium text-ink">{children}</span>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        onChange={(evento) => {
          onChange(evento.target.checked);
        }}
        className="h-6 w-[42px] shrink-0 cursor-pointer accent-pitch"
      />
    </label>
  );
}

/** Encabezado de un grupo de posición, con cuántos jugadores hay en él. */
function TituloDelGrupo({
  position,
  total,
}: {
  position: Position;
  total: number;
}) {
  return (
    <h2 className="mt-1 text-base leading-none">
      {POSITION_GROUPS[position]}{" "}
      <span className="font-sans text-xs font-normal normal-case tracking-normal text-muted">
        {count(total)}
      </span>
    </h2>
  );
}

/** El reparto típico de una plantilla de quince: la silueta que reserva el esqueleto. */
const REPARTO_TIPICO: Record<Position, number> = {
  POR: 2,
  DEF: 5,
  MED: 5,
  DEL: 3,
};

/**
 * La silueta que ocupará la pantalla mientras llega (sección 8.2): la tarjeta del interruptor y
 * los cuatro grupos con sus filas, del mismo alto que las tarjetas que van a sustituirlas.
 */
function EsqueletoDeLaPlantilla() {
  return (
    <>
      <div className="grid gap-1.5 rounded-xl border border-border bg-surface px-3 py-2.5">
        <div className="flex min-h-touch items-center">
          <Skeleton variant="text" className="w-1/2" label="Cargando" />
        </div>
        <Skeleton variant="text" className="w-3/4" label="Cargando" />
      </div>
      {POSITIONS.map((position) => (
        <div key={position} className="grid gap-2.5">
          <Skeleton variant="text" className="mt-1 w-28" label="Cargando" />
          <Skeleton
            variant="row"
            count={REPARTO_TIPICO[position]}
            label={`Cargando ${POSITION_GROUPS[position].toLowerCase()}`}
          />
        </div>
      ))}
    </>
  );
}

/* =========================================================================== pantalla ==== */

export function Squad() {
  const [sort, setSort] = useState<SquadSort>("expected");
  const [conPlan, setConPlan] = useState(false);

  const plantilla = useSquad(conPlan);
  const plan = usePlan();
  const guardarPlan = useUpdatePlan();

  const datos = plantilla.data;
  const jugadores = datos?.players ?? [];

  const grupos = useMemo(
    () => agruparPorPosicion(ordenarPlantilla(jugadores, sort)),
    [jugadores, sort],
  );

  /** El jugador de la plantilla detrás de una fila de `PlayerList`, que solo recibe lo que pinta. */
  const porId = useMemo(
    () => new Map(jugadores.map((jugador) => [jugador.id, jugador])),
    [jugadores],
  );

  function jugadorDe(player: PlayerCardData): SquadPlayer | null {
    return porId.get(player.id) ?? null;
  }

  /** El plan editable. Sin respuesta todavía es un plan vacío, no un botón deshabilitado. */
  const borrador: PlanDraft = toPlanDraft(plan.data);
  const planLeido = plan.data !== undefined;

  /**
   * En qué lado del plan está el jugador. `GET /api/plan` es la fuente; mientras no responde se
   * usa `inPlan`, que basta para marcar la tarjeta aunque no sirva para editar.
   */
  function ladoEnPlan(player: SquadPlayer | null): PlanSide | null {
    if (player === null) return null;
    if (!planLeido) return player.inPlan === "sell" ? "sells" : null;
    return planSideOf(borrador, player.id);
  }

  function accionDePlan(player: SquadPlayer): ReactNode {
    const lado = ladoEnPlan(player);
    const etiqueta = lado === null ? "Vender en plan" : "Quitar del plan";

    return (
      <Button
        size="sm"
        disabled={!planLeido || guardarPlan.isPending}
        // Quince botones con el mismo texto: el nombre completo distingue de cuál es cada uno.
        aria-label={`${etiqueta}: ${player.name}`}
        onClick={() => {
          guardarPlan.mutate(
            lado === null
              ? addToPlan(borrador, "sells", {
                  playerId: player.id,
                  price: player.price,
                })
              : removeFromPlan(borrador, lado, player.id),
          );
        }}
      >
        {etiqueta}
      </Button>
    );
  }

  /** La columna propia de esta pantalla en la tabla de ≥ 768 px. */
  const columnaGanancia: PlayerColumn = {
    header: "Ganancia",
    render: (player) => ganancia(jugadorDe(player)?.gain ?? null),
  };

  const presupuesto: number | null =
    datos === undefined ? null : conPlan ? datos.budgetAfterPlan : datos.budget;
  const hayPlantilla = jugadores.length > 0;
  const planVacio =
    plan.data !== undefined &&
    plan.data.buys.length === 0 &&
    plan.data.sells.length === 0;

  /* ----------------------------------------------------------------- la barra de orden - */

  const barraDeOrden =
    plantilla.isPending || hayPlantilla ? (
      <div
        role="group"
        aria-label="Ordenar la plantilla por"
        // Cuatro chips no caben en 360 px: se desplaza la barra, nunca la página (sección 8.1).
        className="sticky top-[52px] z-10 flex gap-2 overflow-x-auto border-b border-border bg-paper px-3.5 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:px-[18px]"
      >
        {SQUAD_SORTS.map((criterio) => (
          <ToggleChip
            key={criterio.id}
            pressed={sort === criterio.id}
            onClick={() => {
              setSort(criterio.id);
            }}
          >
            {criterio.label}
          </ToggleChip>
        ))}
      </div>
    ) : undefined;

  /* ------------------------------------------------------------------------ el cuerpo - */

  let cuerpo: ReactNode;
  if (plantilla.isPending) {
    cuerpo = <EsqueletoDeLaPlantilla />;
  } else if (datos === undefined) {
    cuerpo = (
      <ErrorState
        title="No se ha podido cargar la plantilla"
        message={describeError(plantilla.error)}
        onRetry={() => void plantilla.refetch()}
      />
    );
  } else if (!hayPlantilla) {
    cuerpo = conPlan ? (
      <EmptyState
        title="El plan deja la plantilla vacía"
        message="Con el plan aplicado no queda ningún jugador. Apaga Ver con plan para volver a tu plantilla."
      />
    ) : (
      <EmptyState
        title="Aún no hay plantilla"
        message="Sincroniza desde Inicio para traer tus jugadores y sus métricas."
        action={
          <ButtonLink to="/" variant="primary">
            Ir a Inicio
          </ButtonLink>
        }
      />
    );
  } else {
    cuerpo = (
      <div
        // Datos anteriores mientras llegan los nuevos: el interruptor cambia la consulta.
        aria-busy={plantilla.isPlaceholderData}
        className="grid gap-2.5"
      >
        {grupos.map((grupo) => (
          <section key={grupo.position} className="grid gap-2.5">
            <TituloDelGrupo
              position={grupo.position}
              total={grupo.players.length}
            />
            <PlayerList
              label={POSITION_GROUPS[grupo.position]}
              players={grupo.players}
              stale={plantilla.isPlaceholderData}
              column={columnaGanancia}
              state={(player) =>
                ladoEnPlan(jugadorDe(player)) === null ? "default" : "inPlan"
              }
              extra={(player) => {
                const jugador = jugadorDe(player);
                if (jugador === null) return null;
                const lado = ladoEnPlan(jugador);
                return (
                  <>
                    <span className="font-mono tabular-nums">
                      Ganancia {dash(jugador.gain, delta)}
                    </span>
                    {/* El borde de acento no puede ser la única señal del plan. */}
                    {lado === null ? null : (
                      <span className="font-semibold text-pitch">
                        {lado === "sells" ? "Venta en plan" : "Compra en plan"}
                      </span>
                    )}
                  </>
                );
              }}
              actions={(player) => {
                const jugador = jugadorDe(player);
                return jugador === null ? null : accionDePlan(jugador);
              }}
            />
          </section>
        ))}
      </div>
    );
  }

  return (
    <Page
      title="Plantilla"
      sticky={barraDeOrden}
      action={
        presupuesto === null ? undefined : (
          <span className="font-mono text-[13px] tabular-nums text-money">
            <span className="sr-only">
              {conPlan ? "Presupuesto con el plan: " : "Presupuesto: "}
            </span>
            <span>{money(presupuesto)}</span>
          </span>
        )
      }
    >
      {presupuesto === null ? null : (
        <section className="grid gap-1.5 rounded-xl border border-border bg-surface px-3 py-2.5">
          <Interruptor checked={conPlan} onChange={setConPlan}>
            Ver con plan
          </Interruptor>
          <p className="text-xs text-muted">
            {conPlan
              ? "Presupuesto con el plan aplicado: "
              : "Presupuesto disponible: "}
            <span className="font-mono tabular-nums text-money">
              {money(presupuesto)}
            </span>
            {" · "}
            {count(jugadores.length)}{" "}
            {jugadores.length === 1 ? "jugador" : "jugadores"}
          </p>
          <p className="text-xs text-muted">
            {planVacio
              ? "El plan está vacío: marca aquí las ventas y añade las compras desde Mercado."
              : conPlan
                ? "Estás viendo la plantilla sin las ventas del plan y con sus compras."
                : "Enciende Ver con plan para ver cómo quedarían la plantilla y el presupuesto."}
          </p>
        </section>
      )}

      {plan.isError ? (
        <ErrorState
          title="No se ha podido leer el plan"
          message={describeError(plan.error)}
          onRetry={() => void plan.refetch()}
        />
      ) : null}

      {plan.data !== undefined && !plan.data.planValid ? (
        <Banner title="El plan no se sostiene">
          <ul className="grid list-disc gap-1 pl-5">
            {plan.data.reasons.map((motivo) => (
              <li key={motivo}>{motivo}</li>
            ))}
          </ul>
        </Banner>
      ) : null}

      {guardarPlan.isError ? (
        <Banner tone="error" title="No se ha podido guardar el plan">
          {describeError(guardarPlan.error)}
        </Banner>
      ) : null}

      {cuerpo}
    </Page>
  );
}
